import { useCallback } from "react";
import { useCreateAuditlog } from "../../hooks/useAuditlog";
import { useUserStore } from "../../stores/user.store";
import { CupStatus } from "../../domain/cup.interface";
import { AnalyteStatus } from "../../domain/analyte.interface";
import { CupDraft, toCupPayload } from "./cupDraft";
import { AnalyteDraft, toAnalytePayload } from "./analyteDraft";

/**
 * SIMS-8861 — the Cups & Analytes audit log (FR-50, FR-7, C-2).
 *
 * One entry per KIND of change, so a change to what a cup detects is never folded into an
 * ordinary edit (AC1): a single cup save can write UPD, ANALYTES_CHG and STATUS_CHG side by side,
 * the model Payers uses for STATUS_CHG. Every code here is declared in the backend's ActionCode
 * and pinned by action-code.audit.spec.ts — an undeclared code is a silent 400.
 *
 * NO PHI (NFR-4). Details name configuration only: cup name and vendor, analyte shortnames and
 * scientific names, field names, status. The actor is stamped by the server from the signed-in
 * user, never sent from here.
 */
export const CUPS_AUDIT_MODULE = "Cups & Analytes";
const OPTION = "Cups & Analytes Management";

export interface CupsAuditEntry {
	actionCode: string;
	action: string;
	detail: string;
}

/** Names repeat across vendors (FR-45), so a cup is always named with its vendor. */
const cupRef = (name: string, vendor: string): string =>
	`“${name.trim()} (${vendor.trim()})”`;

const list = (items: string[]): string => items.join(", ");

const CUP_FIELDS: {
	key: "name" | "vendor" | "display" | "description";
	label: string;
}[] = [
	{ key: "name", label: "name" },
	{ key: "vendor", label: "vendor" },
	{ key: "display", label: "vendor display name" },
	{ key: "description", label: "description" },
];

/**
 * The entries one cup save produces. `before` is the SAVED cup as a draft, or null for a new cup;
 * `shortnameOf` names an analyte id (catalogue first, the cup's own populated list as fallback).
 */
export const cupSaveEntries = (
	before: CupDraft | null,
	after: CupDraft,
	shortnameOf: (id: string) => string,
): CupsAuditEntry[] => {
	const b = before ? toCupPayload(before, "", "") : null;
	const a = toCupPayload(after, "", "");
	const ref = cupRef(a.name, a.vendor);
	const entries: CupsAuditEntry[] = [];

	if (!b) {
		entries.push({
			actionCode: "WEB_CUP_ADD",
			action: "Cup created",
			detail: `Created cup ${ref}${a.status === CupStatus.INACTIVE ? " as inactive" : ""}`,
		});
	} else {
		const changed = CUP_FIELDS.filter(({ key }) => b[key] !== a[key]);
		if (changed.length > 0) {
			const renamed =
				b.name !== a.name || b.vendor !== a.vendor
					? ` (was ${cupRef(b.name, b.vendor)})`
					: "";
			entries.push({
				actionCode: "WEB_CUP_UPD",
				action: "Cup updated",
				detail: `Updated cup ${ref}${renamed} — ${list(changed.map((f) => f.label))}`,
			});
		}
		if (b.status !== a.status) {
			entries.push({
				actionCode: "WEB_CUP_STATUS_CHG",
				action: "Cup status changed",
				detail: `Set cup ${ref} to ${a.status === CupStatus.INACTIVE ? "inactive" : "active"}`,
			});
		}
	}

	// AC1 — what the cup detects, in both directions.
	const was = new Set(b?.analytes ?? []);
	const afterIds = a.analytes ?? [];
	const now = new Set(afterIds);
	const added = afterIds.filter((id) => !was.has(id)).map(shortnameOf);
	const removed = (b?.analytes ?? [])
		.filter((id) => !now.has(id))
		.map(shortnameOf);
	if (added.length > 0 || removed.length > 0) {
		const parts = [
			added.length > 0 ? `added: ${list(added)}` : null,
			removed.length > 0 ? `removed: ${list(removed)}` : null,
		].filter(Boolean);
		entries.push({
			actionCode: "WEB_CUP_ANALYTES_CHG",
			action: "Cup analytes changed",
			detail: `Changed what cup ${ref} detects — ${parts.join("; ")}`,
		});
	}
	return entries;
};

const ANALYTE_FIELDS: {
	key:
		| "shortname"
		| "scientificName"
		| "shortnameDisplay"
		| "scientificDisplay"
		| "type";
	label: string;
}[] = [
	{ key: "shortname", label: "shortname" },
	{ key: "scientificName", label: "scientific name" },
	{ key: "shortnameDisplay", label: "shortname display" },
	{ key: "scientificDisplay", label: "scientific display" },
	{ key: "type", label: "type" },
];

/** The entries one analyte save produces; `before` is null for a new analyte. */
export const analyteSaveEntries = (
	before: AnalyteDraft | null,
	after: AnalyteDraft,
): CupsAuditEntry[] => {
	const b = before ? toAnalytePayload(before, "", "") : null;
	const a = toAnalytePayload(after, "", "");
	const ref = `“${a.shortname}”`;
	if (!b) {
		return [
			{
				actionCode: "WEB_ANALYTE_ADD",
				action: "Analyte created",
				detail: `Created analyte “${a.shortname} — ${a.scientificName}”${a.status === AnalyteStatus.INACTIVE ? " as inactive" : ""}`,
			},
		];
	}
	const entries: CupsAuditEntry[] = [];
	const changed = ANALYTE_FIELDS.filter(({ key }) => b[key] !== a[key]);
	if (changed.length > 0) {
		const renamed =
			b.shortname !== a.shortname ? ` (was “${b.shortname}”)` : "";
		entries.push({
			actionCode: "WEB_ANALYTE_UPD",
			action: "Analyte updated",
			detail: `Updated analyte ${ref}${renamed} — ${list(changed.map((f) => f.label))}`,
		});
	}
	if (b.status !== a.status) {
		entries.push({
			actionCode: "WEB_ANALYTE_STATUS_CHG",
			action: "Analyte status changed",
			detail: `Set analyte ${ref} to ${a.status === AnalyteStatus.INACTIVE ? "inactive" : "active"}`,
		});
	}
	return entries;
};

export const cupDuplicateEntry = (
	source: { name: string; vendor: string },
	copyName: string,
): CupsAuditEntry => ({
	actionCode: "WEB_CUP_DUPLICATE",
	action: "Cup duplicated",
	detail: `Duplicated cup ${cupRef(source.name, source.vendor)} as ${cupRef(copyName, source.vendor)}, inactive`,
});

export const cupDeleteEntry = (cup: {
	name: string;
	vendor: string;
}): CupsAuditEntry => ({
	actionCode: "WEB_CUP_DELETE",
	action: "Cup deleted",
	detail: `Deleted cup ${cupRef(cup.name, cup.vendor)}`,
});

export const analyteDeleteEntry = (analyte: {
	shortname: string;
	scientificName?: string;
}): CupsAuditEntry => ({
	actionCode: "WEB_ANALYTE_DELETE",
	action: "Analyte deleted",
	detail: `Deleted analyte “${analyte.shortname}${analyte.scientificName ? ` — ${analyte.scientificName}` : ""}”`,
});

/**
 * Writes entries AFTER the change succeeded. Never throws: a failed audit write must not turn a
 * completed save into an error the user retries (and duplicates). It is logged instead.
 */
export const useCupsAudit = (): ((
	entries: CupsAuditEntry[],
) => Promise<void>) => {
	const { mutateAsync } = useCreateAuditlog();
	// release_22_10 predates SIMS-7918, so the audit API still reads the actor from
	// `createdBy` in the payload — the same as every other screen on this branch (see
	// Payers). development derives it server-side from the authenticated principal; when
	// SIMS-7918 reaches release this field goes away again.
	const { userInfo } = useUserStore();
	const createdBy = userInfo?.id || "";
	return useCallback(
		async (entries: CupsAuditEntry[]) => {
			for (const entry of entries) {
				try {
					await mutateAsync({
						appType: "WEB_BACK_OFFICE",
						module: CUPS_AUDIT_MODULE,
						option: OPTION,
						createdBy,
						...entry,
					});
				} catch (error) {
					// eslint-disable-next-line no-console
					console.error(
						`[Cups & Analytes] audit log failed (${entry.actionCode}) — the change itself succeeded`,
						error,
					);
				}
			}
		},
		[mutateAsync, createdBy],
	);
};
